import { Alert, Box, Button, Stack } from '@mui/material';
import { Decimal } from 'decimal.js';
import { uniqueId } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import WholePageSpinner from '../../../common/WholePageSpinner';
import { getError } from '../../../helpers/checkout';
import { CheckoutStatus } from '../../../models/CheckoutSession';
import { OtpRequest } from '../../../models/Otp';
import { AetherPaymentRequest } from '../../../models/Payment';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  addPaymentRequest,
  editPaymentRequest,
  updatePaymentRequest,
} from '../../../redux/reducers/checkoutReducer';
import {
  selectAdminSettings,
  selectOrderTotals,
  selectParams,
  selectPaymentRequests,
  selectQuestions,
  selectRemainingBalanceDue,
  selectSettings,
  selectStatus,
  selectWorkingConvenienceFee,
} from '../../../redux/selectors/checkoutSelectors';
import usePlaceOrder from '../usePlaceOrder';
import OtpDialog from './OtpDialog';
import PaymentRequestContent from './PaymentRequestContent';
import PaymentSummaryCard from './PaymentSummaryCard';

export default function PaymentActiveContent() {
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectParams);
  const settings = useAppSelector((state) => selectSettings(state, params?.orderType));
  const status = useAppSelector((state) => selectStatus(state, params));
  const paymentRequests = useAppSelector(selectPaymentRequests);
  const remainingBalance = useAppSelector((state) =>
    selectRemainingBalanceDue(state, params, null),
  );
  const questions = useAppSelector((state) => selectQuestions(state, params));
  const [selected, setSelected] = useState<number | null>(null);
  const [openOtpDialog, setOpenOtpDialog] = useState(false);
  const [newRequestKey, setNewRequestKey] = useState(uniqueId());

  const {
    placeOrder,
    loadingPlaceOrder,
    submitPaymentsLoading,
    submitPaymentsError,
    submitOrderLoading,
    submitOrderError,
    paymentErrors,
  } = usePlaceOrder({ selected, setSelected, setOpenOtpDialog });
  const { fee: workingConvenienceFee, tax: convenienceFeeTax } = useAppSelector(
    selectWorkingConvenienceFee,
  );
  const { total } = useAppSelector((state) => selectOrderTotals(state, params));

  const adminSettings = useAppSelector(selectAdminSettings);

  const totalPaymentAmount = useMemo(
    () => new Decimal(total).plus(workingConvenienceFee).plus(convenienceFeeTax),
    [workingConvenienceFee, convenienceFeeTax, total],
  );

  const onSubmit = ({ index, data }: { index: number | null; data: AetherPaymentRequest }) => {
    if (index === null) {
      dispatch(addPaymentRequest(data));
    } else {
      dispatch(updatePaymentRequest({ index, request: data }));
    }
    setNewRequestKey(uniqueId());
    setSelected(null);
  };

  const handleSelect = (index: number | null) => () => {
    setSelected(index);
    if (index !== null) {
      dispatch(editPaymentRequest({ index }));
    }
  };

  const handleOtpDialogContinue = (otpReq: OtpRequest) => {
    setOpenOtpDialog(false);
    placeOrder(otpReq);
  };

  const handleOtpDialogClose = () => {
    setOpenOtpDialog(false);
  };

  const handlePlaceOrder = () => {
    const needOtp = paymentRequests.some((request) => request.otpEnabled);
    if (needOtp) {
      setOpenOtpDialog(true);
    } else {
      placeOrder();
    }
  };

  const processPaymentInstructionsText = useCallback(
    (text: string | undefined): string => {
      if (!text) return '';

      return text.replace(/\[\[(.*?)\]\]/g, (match, tagContent) => {
        if (tagContent.startsWith('BALANCE-DUE-')) {
          const percentageStr = tagContent.replace('BALANCE-DUE-', '');
          const percentage = Math.max(0, Math.min(1, parseFloat(percentageStr) / 100));

          if (!Number.isNaN(percentage)) {
            const calculatedAmount = totalPaymentAmount.toNumber() * percentage;
            return calculatedAmount.toFixed(2);
          }
          return match;
        }

        if (
          tagContent.startsWith('PAYROLL-DEDUCTION') &&
          adminSettings.isPayrollDeductionTagEnabled
        ) {
          const percentage =
            100 -
            (adminSettings.isVisualDiscountEnabled
              ? adminSettings.visualDiscountPercentage ?? 100
              : 100);
          const totalDeduction = new Decimal(totalPaymentAmount).mul(percentage ?? 100).div(100);
          const question = questions?.find(
            (q) => q.question === settings.payrollDeductionTagQuestionName,
          );
          if (!question) {
            return 'N/A';
          }
          const input = Number(question.choiceValue || question.value);
          if (isNaN(input) || input <= 0) {
            return 'N/A';
          }
          return totalDeduction.div(input).toFixed(2);
        }
        return match;
      });
    },
    [totalPaymentAmount, adminSettings, questions, settings],
  );

  return (
    <Box>
      {(submitPaymentsLoading || loadingPlaceOrder || submitOrderLoading) && (
        <WholePageSpinner text="Submitting Order" warningText="Please do not refresh the page" />
      )}
      <OtpDialog
        open={openOtpDialog}
        onClose={handleOtpDialogClose}
        onContinue={handleOtpDialogContinue}
      />
      <Stack direction={'column'} spacing={2}>
        {status !== CheckoutStatus.PENDING_SUBMISSION && settings?.paymentInstructionsEnabled && (
          <Alert severity="info">
            {processPaymentInstructionsText(settings.paymentInstructionsText)}
          </Alert>
        )}
        {paymentRequests.map((_, index) =>
          selected === index ? (
            <PaymentRequestContent
              key={index}
              index={index}
              errorMessage={paymentErrors[index]}
              onEdit={handleSelect(index)}
              onSubmit={onSubmit}
            />
          ) : (
            <PaymentSummaryCard
              key={index}
              payment={paymentRequests[index]}
              onEdit={handleSelect(index)}
            />
          ),
        )}
        {selected === null && remainingBalance > 0 && (
          <PaymentRequestContent
            key={newRequestKey}
            index={null}
            onEdit={handleSelect(null)}
            onSubmit={onSubmit}
          />
        )}
        <Button
          data-testid="place-order-btn"
          variant="contained"
          size={'large'}
          disabled={selected !== null || remainingBalance !== 0}
          loading={submitPaymentsLoading || submitOrderLoading || loadingPlaceOrder}
          onClick={handlePlaceOrder}
        >
          {settings.submitButtonLabel}
        </Button>
        {submitPaymentsError && (
          <Alert severity="error">Error Processing Payments: {getError(submitPaymentsError)}</Alert>
        )}
        {submitOrderError && (
          <Alert severity="error">Error Submitting Order: {getError(submitOrderError)}</Alert>
        )}
      </Stack>
    </Box>
  );
}
